// -----------------------------------------------------------------------
// <copyright file="GetHelpLoggingRequest.ts" company="Microsoft">
//     copyright (c) Microsoft. All rights reserved.
// </copyright>
// -----------------------------------------------------------------------

/**
 * Log level (Reused from Osefo)
 */
export enum LogLevel {
  Trace = 0,
  Info = 1,
  Comment = 2,
  Warning = 4,
  Error = 6,
  Critical = 8,
}

// Remember you when add a tagId here, you also need to list it on
// Osefo.Contract\GetHelp\ValidationAttributes\TagIdAttribute.cs
export enum TagId {
  ResourceJsonLoadError = 513926,
  ResourceStringLoadError = 513928,
  StringLoaderException = 513947,
  LandingOnEmerald = 502305, // landing on Emerald
  ClickedKnownIssueExpand = 508908, // When expand of Known Issue was clicked
  LoadCommonSearchesSuccess = 512249, // When common search queries are loaded successfully
  LoadCommonSearchesError = 512250, // When common searches queries are loaded unsuccessfully
  CommonSearchesQueryClicked = 512251, // Send a query to search box from Common Searches
  LoadCommonSolutionsSuccess = 502307, // When common solution is received successfully
  LoadCommonSolutionsError = 502308, // When common solution is failed to download
  ClickingOnSearch = 502309, // Clicking on search
  ResultsReceived = 502310, // When result is received from Emerald SPA successfully
  ResultsReceiveError = 502311, // When failed to receive result from Emerald SPA
  ClickedBcqrReadArticle = 502312, // Clicked Read article from BCQR result
  BCQRCoverage = 512303, // Logs details of BCQR coverage by source
  ExpandBCQRArticles = 512257, // Clicked Expanded and Collapse of BCQR Article in New Layout
  ClearingSearchBox = 502314, // Clicking on X\Esc of SearchBox control
  UserFeedbackResult = 502315, // User feedback result - Did this resolve your problem?
  GenericError = 502316, // TagId for LogLevel: LogLevel.Error
  AlchemyInsightsReceived = 502317, // There is an Alchemy Insight article is returned.
  KnownIssueReceived = 502318, // Solution for known issue query received
  KnownIssueReceivedError = 502319, // Error in receiving solution for known issue query
  AnnouncementReceived = 502320, // Solution for Announcement query received
  AnnouncementReceivedError = 502321, // Error in receiving solution for Announcement
  AutoSuggestionsClicked = 502334, // Autosuggestions received successfully
  AutoSuggestionsReceivedError = 505718, // Failed to retrieve auto suggestion
  RelatedQueryClicked = 502341, // Clicked a link from Related Queries
  NotifyParentSearchCompletedSuccess = 502342, // NotifyParent that search completed success
  NotifyParentSearchCompletedError = 502343, // NotifyParent that search completed error
  CommercialSmallBusinessUrlClicked = 502344, // Small Business link clicked on Commercial UI
  CommercialItProUrlClicked = 502345, // IT Pro link clicked on Commercial UI
  CommercialDeploymentGuideUrlClicked = 502351, // Advanced Deployment Guide link
  AlchemyFetchApiErrorUsingFallbackTheme = 512342, // To track fallback experience usage
  AlchemyThemeRoutingDataReceivedError = 502350,
  AlchemyThemeRoutingDataReceivedFetchError = 508911, // To track API failures due to network issues on frontend
  AlchemyMultiTurnContentResponseReceived = 505691,
  AlchemyMultiTurnContentError = 505696,
  AlchemyMultiTurnContentFetchError = 508910, // To track API failure due to network issues on frontend
  DiagnosticSessionApiError = 505695, // Get Diagnostic session Id failed
  DiagnosticSessionApiThrottlingError = 512253,
  RunDiagnosticInvoked = 505697, // DUPE with onDafDiagnosticsStarted
  IntentGraphNodeRendered = 505698,
  authorDefinedCustomActionInvoked = 505699,
  diagnosticResultsObtained = 505700, // DUPE with onDafDiagnosticsCompleted or onDafDiagnosticsFailure
  GetAvailableModalitiesError = 505721, // Error occured while getting available modalities from SMC API
  CreateRaveTicketError = 505722, // Error occured while creating a Rave tikcet using SMC API
  ShowEmeraldContactUX = 505723,
  EmeraldContactButtonClicked = 505724,
  ThemeConfirmButtonClicked = 505725,
  AuthResponseReceived = 505726,
  SupportRequestError = 505727,
  SupportRequestComplete = 505728,
  ClientContextSentToAlchemy = 505729, // Log the clientContext object that goes with get solutions API to Alchemy
  TicketCreationPageLoaded = 505730,
  ContactMeButtonFromTicketCreationPageClicked = 505731,
  CancelOrBackButtonFromTicketCreationPageClicked = 505732,
  RetryButtonFromTicketErrorPageClicked = 505733,
  GetModalitiesCallResult = 505734,
  CreateTicketCallResult = 505735,
  TicketConfirmationPageLoaded = 505736,
  OpenItNowLinkFromTicketConfirmationPageClicked = 505737,
  DoneButtonFromTicketConfirmationPageClicked = 505738,
  TicketErrorPageLoaded = 505739,
  SkylightUpgradePageLanded = 508901,
  SkylightUpgradeWebLinkClicked = 508902,
  SkylightUpgradeStoreButtonClicked = 508903,
  GetAvailableModalitiesV2Error = 508904,
  ThemeRoutingDataCallResult = 508905,
  GetModalitiesV2CallResult = 508906,
  SignInButonClicked = 508912,
  TokenRequestedToGetCategoryOptions = 508913,
  CreateTicketSubmitButtonClicked = 509716,
  ThemeLevelSelectionChanged = 509686,
  AgentModalityClicked = 509695,
  NonAgentModalityClicked = 509696,
  SilentLoginFailed = 509717,
  GetCallSuccess = 509718,
  C2CThrottlingError = 508981,
  C2CThrottlingErrorLinkClicked = 512242,
  SMCNonStatusCodeError = 512254,
  CommonSolutionsClicked = 508909,
  DegradedExperience = 512281,
  // Extra logging being added for investigation purpose
  Calling_ThemeRoutingWrapper = 508914,
  Calling_ModalitiesV2Wrapper = 508915,
  Calling_TicketCreationWrapper = 508916,
  Calling_httpService_ThemeRouting = 508917,
  Calling_HttpService_ModalitiesV2 = 508918,
  Calling_Get = 508919,
  Calling_Post = 508920,
  Get_Response = 508921,
  Post_Response = 508922,
  ReturningBackupThemeRouting = 508923,
  Reload_Categories = 508924,
  // Logging for SAF actions
  GetAlchemySAFActionSchemaInvoked = 508925,
  ExecuteAlchemySAFActionInvoked = 508926,
  GetSAFSchemaResponseFailed = 508927,
  GetSAFExecutionResponseFailed = 508928,
  GetSAFExecutionNoParameters = 508929,
  ThemeConfirmButtonDisplayed = 508947,
  ReturningAxiosBackupThemeRouting = 508949,
  unAuthLinkClicked = 508950,
  backButtonFromUnAuthClicked = 508951,
  UnAuthSupportTicketLink = 508952,
  UnAuthPayloadReceived = 508953,
  UnAuthPayloadFailedToReceive = 508954,
  NewGuidGenerated = 508955,
  AuthError = 508956,
  UnAuthLinkDisplayed = 508957,
  AnnouncementDismissed = 508959,
  AnnouncementLinkClicked = 508960,
  // Get Current Sub-Feature Flights
  GetCurrent_SubExperienceReceived = 508962,
  GetCurrent_AtLaunch_NotificationDisplayed = 508963,
  GetCurrent_AtLaunch_InstallButtonClicked = 508964,
  GetCurrent_AtLaunch_CloseButtonClicked = 508965,
  GetCurrent_AfterSearch_NotificationDisplayed = 508966,
  GetCurrent_AfterSearch_NotificationNotDisplayed = 508967,
  GetCurrent_AfterSearch_InstallButtonClicked = 508968,
  GetCurrent_AfterSearch_NotNowLinkClicked = 508969,
  GetCurrent_AsModality_NotificationDisplayed = 508970,
  GetCurrent_AsModality_NotificationNotDisplayed = 508971,
  GetCurrent_AsModality_InstallButtonClicked = 508972,
  GetCurrent_AsModality_CloseButtonClicked = 508973,
  GetCurrent_Error_MultipleSubExperienceReceived = 508980,
  UpSell_RefreshSupportActionsClicked = 508982,
  UpSell_ReviewPlanLinkClicked = 508983,
  UpSell_ModalityReceived = 508984,
  UpSell_UpSellFlightDecision = 508985,
  ExperienceManagerResponseReceived = 505692,
  AboutButtonClicked = 508986,
  FeedbackButtonClicked = 508987,
  OCVFeedbackSDKInitSuccess = 512290,
  OCVFeedbackSDKInitError = 512292,
  OCVFeedbackSendSuccess = 512291,
  OCVFeedbackSendError = 512293,
  OCVFeedbackDismissed = 512294,
  InAppRatingClicked = 508992,
  HttpServiceRetry = 513221,
  // Campaign tags
  NPSCampaign_GetSilentLogIn = 512782,
  NPSCampaign_CaptureUserLicenses = 512783,
  NPSCampaign_UserLicenseApiException = 512785,
  NPSCampaign_UserLicenseFailedToFetch = 512786,
  NPSCampaign_CaptureFlightingDecisions = 512787,
  NPSCampaign_DeviceIdVerification = 512788,
  NPSCampaign_GetEcsConfiguration = 512789,
  NPSCampaign_EcsApiAttempt = 512790,
  NPSCampaign_EcsApiException = 512791,
  NPSCampaign_EcsCallError = 512792,
  NPSCampaign_UserLicenseApiAttempt = 512793,
  NPSCampaign_UserLicenseCallError = 512794,
  SMCGetBasicUserInfoInit = 512301, // Request basic user info by SMC API
  SMCGetBasicUserInfoError = 512302, // Error occured in request of basic user info by SMC API
  FeatureFlightingError = 512246,
  FeatureFlightingReceived = 512247,
  FeatureFlightingFetchError = 512248,
  NoModalitiesReturned = 512243,
  BackButtonFromArticleReader = 502347,
  ArticleRequested = 512252,
  ArticleLoadingFinished = 502322,
  ErrorLoadingArticle = 502323,
  DataPassed = 502324,
  SafInternalResponse = 512255,
  StartupDafExecutionError = 512288,
  StartupDafExecutionResult = 512289,
  OEMModalityQualification = 512234,
  AlchemyGptContentError = 518893,
  AlchemyGptContentFetchError = 518894,
  MismatchPreselectedCategories = 512335,
  CategoriesPageSkipOrNot = 512336,
  VSBAlertLoaded = 512339,
  VSBCTAClicked = 512340,
  VSBAlertDismissed = 512341,
  ServiceRequestPageLoaded = 512342,
}

export enum IntentGraphLogDataTagId {
  onJsonDocumentValidationError = 505701,
  onChoiceClick = 505702,
  onComponentError = 505703,
  onContentRetrievalError = 505704,
  onDafDiagnosticsStarted = 505706, // DUPE with RunDiagnosticInvoked
  onDafDiagnosticsCompleted = 505707, // DUPE with diagnosticResultsObtained
  onDafDiagnosticsFailure = 505717, // DUPE with diagnosticResultsObtained
  onDafDiagnosticsActionTimeout = 505708,
  onDafParamValidationError = 505710,
  onConditionEvaluationFailed = 505714,
  onGetDiagnosticsResultFailed = 508933,
  onGetDiagnosticsResultTimeout = 508934,
  onGetDiagnosticsResultSuceededWithNext = 508935,
  onStartDiagnosticsRun = 508936,
  onDiagnosticsRerun = 508937,
  onDiagnosticResourceReturned = 508938,
  onDiagnosticResourceRetrivalFailure = 508939,
  onSafDiagnosticRendered = 508940,
  onSAfActionRunFailed = 508941,
  onSAfSchemaFetchFailed = 508942,
  onComponentRenderd = 508943,
  onSafDiagnosticsFailure = 508944,
  onLinkClick = 508993,
  onRelatedDialogFetchFailed = 508994,
  onRedirectNodeRendered = 508995,
  onRedirectNodeTargetDialogIdMissing = 508996,
  onRelatedDialogFetchSuccess = 508997,
}

/**
 * GetHelpLoggingRequest http request payload
 */
export type GetHelpLoggingRequest = {
  /**
   * Event tag Id
   * @type {number}
   */
  TagId: number;

  /**
   * Log level
   * @type {LogLevel}
   */
  LogLevel: LogLevel;

  /**
   * Event message
   * @type {string}
   */
  Message: string;

  /**
   * Adhoc0 field
   * @type {string}
   */
  Adhoc0?: string;

  /**
   * Adhoc1 field
   * @type {string}
   */
  Adhoc1?: string;

  /**
   * Adhoc2 field
   * @type {string}
   */
  Adhoc2?: string;

  /**
   * Adhoc3 field
   * @type {string}
   */
  Adhoc3?: string;

  /**
   * Adhoc4 field
   * @type {string}
   */
  Adhoc4?: string;

  /**
   * Adhoc5 field
   * @type {string}
   */
  Adhoc5?: string;
};
